import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Star, ChevronRight, Quote, Award, ShieldCheck, HeartPulse, Clock } from "lucide-react";
import HeroCarousel from "@/components/site/HeroCarousel";
import { useSettings } from "@/context/SettingsContext";
import { resolveImage } from "@/lib/api";

const SectionKicker = ({ children }) => (
  <div className="inline-flex items-center gap-2 mb-3">
    <span className="w-8 h-px bg-accent" />
    <span className="text-[11px] tracking-[0.3em] text-accent font-semibold uppercase">{children}</span>
  </div>
);

const Home = () => {
  const { settings } = useSettings();
  const s = settings;

  return (
    <div data-testid="home-page">
      <HeroCarousel />

      {/* Quick cards floating */}
      <section className="relative -mt-16 z-10 container-pad">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {s.quick_cards?.map((c, i) => (
            <motion.a
              key={i}
              href={c.link}
              target={c.link?.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group bg-white rounded-2xl p-5 ring-medical hover:shadow-xl transition-all hover:-translate-y-1 border border-border/60"
              data-testid={`quick-card-${i}`}
            >
              <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-secondary to-secondary/40 flex items-center justify-center mb-3 group-hover:from-accent/20 group-hover:to-accent/10 transition">
                <img src={resolveImage(c.image)} alt={c.title} className="h-9 w-9 object-contain" />
              </div>
              <div className="font-display text-base md:text-lg text-primary font-semibold mb-1">{c.title}</div>
              <p className="text-[12px] text-muted-foreground mb-2 leading-relaxed line-clamp-2">{c.description}</p>
              <span className="text-[12px] font-semibold text-accent inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Saiba Mais <ChevronRight className="w-3 h-3" />
              </span>
            </motion.a>
          ))}
        </div>
      </section>

      {/* About */}
      <section className="container-pad py-16 md:py-20 relative overflow-hidden" data-testid="home-about">
        <div className="absolute top-20 -left-32 w-72 h-72 bg-accent blob" />
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center relative">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden ring-medical">
              <img src={resolveImage(s.doctor?.photo)} alt={s.doctor?.name} className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-4 md:-right-8 bg-white p-5 rounded-2xl max-w-[220px] shadow-2xl border border-border">
              <div className="flex items-center gap-1.5 mb-1 text-accent">
                {Array.from({ length: 5 }).map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-current" />)}
              </div>
              <div className="font-display text-xl text-primary font-semibold">12+</div>
              <div className="text-xs text-muted-foreground">avaliações no Google</div>
            </div>
            <div className="hidden md:flex absolute -top-5 -right-5 bg-accent text-accent-foreground px-4 py-3 rounded-full items-center gap-2 shadow-xl">
              <Award className="w-4 h-4" />
              <span className="text-xs font-semibold">Otorrinolaringologista</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <SectionKicker>Bem-vindo ao site</SectionKicker>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-primary leading-tight mb-5 text-balance font-light">
              Cuidado humanizado em <span className="text-gradient-medical font-medium">Otorrinolaringologia</span>
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">{s.doctor?.bio_short}</p>
            <div className="flex items-center gap-2 mb-6">
              <ShieldCheck className="w-4 h-4 text-accent" />
              <span className="text-sm font-medium text-primary">{s.doctor?.crm}</span>
            </div>

            <div className="bg-gradient-to-br from-secondary/60 to-secondary/20 rounded-2xl p-5 mb-6 relative border-l-4 border-accent">
              <Quote className="absolute top-3 right-3 w-7 h-7 text-accent/30" />
              <p className="text-primary font-display italic text-base md:text-lg leading-relaxed">
                "{s.doctor?.quote}"
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link to="/sobre" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all hover:-translate-y-0.5 text-sm font-medium" data-testid="home-about-cta">
                Conhecer o médico <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contato" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground transition-all text-sm font-medium">
                Agendar consulta
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features strip */}
      <section className="bg-primary text-primary-foreground py-10 relative overflow-hidden">
        <div className="container-pad grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Award, label: "Médico Especialista", subLabel: "Otorrinolaringologista", value: "CRM-MT 10.467 RQE 6533" },
            { icon: HeartPulse, label: "Atendimento", value: "Humanizado" },
            { icon: ShieldCheck, label: "Procedimentos", value: "Última geração" },
            { icon: Clock, label: "Agenda", value: "Plantão 24h" },
          ].map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-accent flex items-center justify-center shrink-0">
                <f.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-primary-foreground/60">{f.label}</div>
                {f.subLabel && <div className="text-[11px] uppercase tracking-widest text-primary-foreground/70 mt-0.5">{f.subLabel}</div>}
                <div className="font-display font-semibold text-sm md:text-base leading-tight">{f.value}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="bg-secondary/40 py-16 md:py-20 relative overflow-hidden" data-testid="home-services">
        <div className="absolute inset-0 pattern-dots opacity-50" />
        <div className="container-pad relative">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <SectionKicker>Nossos serviços</SectionKicker>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-primary leading-tight max-w-xl text-balance font-light">
                Serviços médicos especializados
              </h2>
            </div>
            <Link to="/servicos" className="text-sm font-medium text-primary inline-flex items-center gap-2 hover:text-accent transition">
              Ver todos <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {s.services?.map((svc, i) => (
              <motion.a
                key={i}
                href={svc.link}
                target={svc.link?.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-white rounded-3xl overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-1 border border-border/60"
                data-testid={`service-card-${i}`}
              >
                <div className="relative aspect-[5/4] overflow-hidden bg-gradient-to-br from-secondary to-white p-6">
                  <img src={resolveImage(svc.image)} alt={svc.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-4 left-4 bg-primary text-primary-foreground text-[10px] tracking-widest uppercase px-3 py-1 rounded-full">0{i + 1}</div>
                </div>
                <div className="p-6">
                  <div className="font-display text-xl md:text-2xl text-primary font-semibold mb-2">{svc.title}</div>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{svc.description}</p>
                  <span className="text-sm font-semibold text-accent inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Saiba Mais <ChevronRight className="w-4 h-4" />
                  </span>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Units & Schedule */}
      <section className="container-pad py-16 md:py-20" data-testid="home-units">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-12">
          <div>
            <SectionKicker>Unidades de atendimento</SectionKicker>
            <h2 className="font-display text-3xl md:text-4xl text-primary mb-3 text-balance font-light">Onde você pode me encontrar</h2>
            <p className="text-sm text-muted-foreground mb-6">Atendimento médico especializado em Otorrinolaringologia em Cuiabá-MT.</p>
            <div className="space-y-3">
              {s.units?.map((u, i) => (
                <a
                  key={i}
                  href={u.link}
                  target="_blank"
                  rel="noreferrer"
                  className="block p-5 rounded-2xl border border-border bg-white hover:border-accent hover:shadow-lg transition group"
                  data-testid={`unit-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-secondary to-white group-hover:from-accent group-hover:to-accent/80 group-hover:text-white transition flex items-center justify-center shrink-0">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-display text-base md:text-lg text-primary font-semibold mb-1">{u.name}</div>
                      <p className="text-xs text-muted-foreground">{u.address}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-accent transition shrink-0 mt-1.5" />
                  </div>
                </a>
              ))}
            </div>
          </div>

          <div>
            <SectionKicker>Horários de atendimento</SectionKicker>
            <h2 className="font-display text-3xl md:text-4xl text-primary mb-6 text-balance font-light">Agenda da semana</h2>
            <div className="rounded-2xl overflow-hidden border border-border bg-white">
              {s.schedule?.map((sc, i) => {
                const closed = /fechad|fechada/i.test(sc.hours);
                return (
                  <div key={i} className={`flex items-center justify-between px-5 py-3.5 ${i !== 0 ? "border-t border-border" : ""} ${closed ? "bg-muted/40" : ""}`} data-testid={`schedule-row-${i}`}>
                    <span className="text-sm font-medium text-primary">{sc.day}</span>
                    <span className={`text-sm font-medium ${closed ? "text-muted-foreground" : "text-accent"}`}>{sc.hours}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-primary text-primary-foreground py-16 md:py-20 relative overflow-hidden" data-testid="home-testimonials">
        <div className="absolute inset-0 pattern-grid opacity-40" />
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-accent blob" />
        <div className="container-pad relative">
          <SectionKicker>Depoimentos</SectionKicker>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl mb-3 text-balance font-light">Pacientes satisfeitos</h2>
          <p className="text-sm text-primary-foreground/60 mb-10">Avaliações 5 estrelas no Google.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {s.testimonials?.slice(0, 6).map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-primary-foreground/[0.06] backdrop-blur border border-primary-foreground/10 rounded-2xl p-6 hover:border-accent/40 transition"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex gap-1 mb-3 text-accent">
                  {Array.from({ length: t.rating || 5 }).map((_, k) => (
                    <Star key={k} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-sm leading-relaxed mb-4 text-primary-foreground/90 line-clamp-5">{t.text}</p>
                <div className="text-xs text-primary-foreground/60 border-t border-primary-foreground/10 pt-3">— {t.name}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-pad py-14 md:py-16" data-testid="home-cta">
        <div className="bg-gradient-to-br from-accent via-accent to-emerald-600 rounded-[2rem] p-8 md:p-14 text-accent-foreground relative overflow-hidden">
          <div className="absolute inset-0 pattern-dots opacity-20" />
          <div className="relative z-10 grid lg:grid-cols-3 gap-6 items-center">
            <div className="lg:col-span-2">
              <h2 className="font-display text-2xl md:text-4xl mb-3 text-balance font-light">Cuide da sua saúde e da sua família.</h2>
              <p className="text-base opacity-90">Entre em contato e faça seu agendamento hoje mesmo.</p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link to="/contato" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-primary font-medium hover:-translate-y-0.5 transition-all text-sm" data-testid="cta-contact-btn">
                Agendar agora <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={`https://wa.me/${s.contact?.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-white text-white font-medium hover:bg-white hover:text-primary transition-all text-sm">
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
